'use client'

import RegistrationCheckFormHeader from '@/components/form/FormHeader'
import Container from '@/components/layout/Container'
import { useUserInteractivity } from '@/providers/user-interactivity-provider'
import { Activity, ActivityStates } from '@/types/activities'
import {
    AddIcon,
    ChevronRightIcon,
    Button,
    ButtonIconPosition,
    ButtonVariants,
    DictionaryText,
    InlineNotification,
    NotificationVariant,
    Spinner,
} from '@lib-components'
import { use, useEffect } from 'react'
import { Activities } from './Activities'
import { Routes } from '@/constants/routeConstants'
import { useRouter } from 'next/navigation'
import { StepIDs } from '@/constants/stepConstants'
import { AppContext } from '@/providers/app-provider'
import PagePanel from '../layout/PagePanel'
import { RegistrationCheckContext } from '@/providers/registration-check-provider'
import { RegistrationDataContext } from '@/providers/registration-data-provider/registration-data-provider'

const MESSAGE_KEYS = {
    START_REGISTRATION: 'STO.CHECK.ACTIONS.START_REGISTRATION',
    ADD_ACTIVITY: 'STO.CHECK.ACTIONS.ADD_ACTIVITY',
    TRY_AGAIN: 'STO.CHECK.TRY_AGAIN',
    FETCH_QUESTIONS_FAILED: 'STO.CHECK.ERROR.FETCH_QUESTIONS_FAILED',
}

/**
 * For documentation see https://confluence.hosted-tools.com/display/STICHTINGOPEN/Registratie+check
 */
export const RegistrationCheck = () => {
    const router = useRouter()
    const { registerFirstInteraction } = useUserInteractivity()
    const {
        activities,
        questions,
        setActivities,
        isSubmitting,
        setIsSubmitting,
        isLoadingQuestions,
        hasFetchError,
        getQuestions,
        isAddActivityButtonVisible,
        isStartRegistrationButtonVisible,
    } = use(RegistrationCheckContext)
    const { setNonContractRegistrationKeys, setRegistrationKeys, setDepartmentTypes } = use(RegistrationDataContext)

    const { goToStep } = use(AppContext)

    const updateActivity = (activity: Activity): void => {
        startFlowOnFirstAnswer(activity)

        if (!activities.find((stateActivity) => stateActivity.id === activity.id)) return

        const newActivities = activities.map((stateActivity) =>
            stateActivity.id === activity.id ? { ...stateActivity, ...activity } : stateActivity,
        )
        setActivities([...newActivities])
    }

    const startFlowOnFirstAnswer = (activity: Activity): void => {
        if (activity.answerResults?.length > 0) registerFirstInteraction()
    }

    const addActivity = (): void => {
        const generatedId = Math.floor(Math.random() * 100000)
        const newActivities = [
            ...activities,
            {
                id: generatedId,
                registrationKeys: [],
                nonContractRegistrationKeys: [],
                answerResults: [],
                departmentTypes: [],
                status: ActivityStates.IN_PROGRESS,
            },
        ]
        setActivities([...newActivities])
    }

    const deleteActivity = (activity: Activity): void => {
        if (!activity) return

        const newActivities = [...activities].filter((newActivity) => newActivity.id !== activity.id)
        setActivities([...newActivities])
    }

    const nextHandler = () => {
        if (isSubmitting) return
        setIsSubmitting(true)
        setNonContractRegistrationKeys(activities.flatMap((a) => a.nonContractRegistrationKeys))
        setRegistrationKeys(activities.flatMap((a) => a.registrationKeys))
        setDepartmentTypes([...new Set(activities.flatMap((a) => a.departmentTypes))])
        goToStep(StepIDs.TWO)
        router.push(Routes.OVERVIEW)
    }

    useEffect(() => {
        if (activities.length === 0) addActivity()
    }, [])

    return (
        <main className="pb-6" data-cy="registration-check-start-page">
            <RegistrationCheckFormHeader />
            {isLoadingQuestions && (
                <div className="flex w-full items-center justify-center py-6 border-b-[1px] border-shade-opacity-200">
                    <Spinner />
                </div>
            )}
            {hasFetchError && !isLoadingQuestions && (
                <PagePanel className="py-6">
                    <InlineNotification
                        message={<DictionaryText textKey={MESSAGE_KEYS.FETCH_QUESTIONS_FAILED} />}
                        variant={NotificationVariant.ERROR}
                        dismissable={false}
                        id="fetch-questions-error"
                        action={
                            <Button variant={ButtonVariants.QUIET} onClick={getQuestions}>
                                <DictionaryText textKey={MESSAGE_KEYS.TRY_AGAIN} />
                            </Button>
                        }
                    />
                </PagePanel>
            )}

            {questions && questions.length > 0 && (
                <Activities updateActivity={updateActivity} deleteActivity={deleteActivity} />
            )}

            <Container size={960} className="flex gap-2 items-center w-full mt-8 pb-4">
                {isAddActivityButtonVisible && (
                    <Button
                        variant={ButtonVariants.SECONDARY}
                        icon={<AddIcon />}
                        iconPosition={ButtonIconPosition.LEFT}
                        onClick={addActivity}
                        data-cy="button-add-activity"
                    >
                        <DictionaryText textKey={MESSAGE_KEYS.ADD_ACTIVITY} />
                    </Button>
                )}
                {isStartRegistrationButtonVisible && (
                    <Button
                        variant={ButtonVariants.PRIMARY}
                        icon={<ChevronRightIcon />}
                        iconPosition={ButtonIconPosition.RIGHT}
                        onClick={nextHandler}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        data-cy="check-page-button-start"
                    >
                        <DictionaryText textKey={MESSAGE_KEYS.START_REGISTRATION} />
                    </Button>
                )}
            </Container>
        </main>
    )
}
